import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  changeUiLanguage,
  readStoredUiLanguage,
  resolveUiLanguageFromLocale,
  type UiLanguage,
} from "./index";
import { useDisplayLocalization } from "../lib/datetime/DisplayLocalizationProvider";

/**
 * Keeps i18n language aligned with an explicit UI preference, otherwise with
 * the customer profile locale (tr-TR → tr, en-US → en).
 */
export function useSyncUiLanguageFromCustomerLocale() {
  const { locale, ready } = useDisplayLocalization();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!ready) {
      return;
    }

    const stored = readStoredUiLanguage();
    const next: UiLanguage = stored ?? resolveUiLanguageFromLocale(locale);
    if (i18n.language !== next) {
      void changeUiLanguage(next);
    }
  }, [ready, locale, i18n]);
}

export function useUiLanguage(): {
  language: UiLanguage;
  setLanguage: (language: UiLanguage) => Promise<void>;
} {
  const { i18n } = useTranslation();
  const language = (i18n.language?.startsWith("tr") ? "tr" : "en") as UiLanguage;

  return {
    language,
    setLanguage: changeUiLanguage,
  };
}
